import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { CoachState, CoachStore, Flow, Step } from './types.js';
import { MAX_SKIPPED_STEPS, emptyCoachState } from './types.js';

export type CoachBudget = {
  /** Ambient flows allowed per session. One is the right default: the student
   *  opened the app to tick a habit, and every flow after the first is us
   *  interrupting that twice. Onboarding ignores this. */
  ambientPerSession: number;
};

export const defaultBudget: CoachBudget = { ambientPerSession: 1 };

export type CoachApi = {
  /**
   * Ask for a flow to run. It may not — it may be seen already, or another
   * may be on screen, or the ambient budget may be spent.
   *
   * `offer` rather than `start` on purpose: the host knows the moment is
   * right, the library knows whether the student can bear another one, and
   * putting that judgement in one place is what stops week three becoming a
   * gauntlet of popups.
   */
  offer(flowId: string): void;
  /** Tell the coach something happened, advancing any step waiting on it. */
  completed(action: string): void;
  /** Abandon the running flow. Marked seen — it was offered and refused. */
  dismiss(): void;
  /** Advance a `'next'` step. */
  next(): void;
  /** Step over an anchor that never appeared. Called by the renderer. */
  skip(): void;
  /** Record an `input` step's answer and advance. */
  answer(value: string): void;
  /** The step on screen, or null. */
  current: { flow: Flow; step: Step; index: number } | null;
  /** Answers collected so far, keyed by flow id. */
  answers: Record<string, string>;
};

const CoachContext = createContext<CoachApi | null>(null);

export type CoachProviderProps = {
  /**
   * Called when an `input` step is answered.
   *
   * The library stores the value and moves on; what it MEANS is the host's
   * business. This is what lets a flow capture a habit's floor without this
   * package ever learning what a habit is.
   */
  onAnswer?: (flowId: string, value: string) => void;
  flows: Flow[];
  store: CoachStore;
  budget?: CoachBudget;
  children: ReactNode;
};

export function CoachProvider({
  flows,
  store,
  budget = defaultBudget,
  onAnswer,
  children,
}: CoachProviderProps) {
  const [state, setState] = useState<CoachState>(emptyCoachState);
  const [loaded, setLoaded] = useState(false);
  const skipped = useRef(0);
  const ambientRun = useRef(0);

  const byId = useMemo(() => new Map(flows.map((flow) => [flow.id, flow])), [flows]);

  // Nothing may run before the store has been read, or a returning student is
  // taught the app again on every cold start.
  useEffect(() => {
    let live = true;
    void store.read().then((read) => {
      if (!live) return;
      setState(read);
      setLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [store]);

  const persist = useCallback(
    (next: CoachState) => {
      setState(next);
      void store.write(next);
    },
    [store],
  );

  const active = state.active;
  const flow = active ? (byId.get(active.flowId) ?? null) : null;
  // A flow whose id is in the store but no longer in `flows` has been deleted
  // between releases. `?? null` is what stops that from being a crash.
  const step = flow ? (flow.steps[active?.step ?? 0] ?? null) : null;

  const finish = useCallback(
    (flowId: string) => {
      const { active: _drop, ...rest } = state;
      persist({ ...rest, seen: [...state.seen, flowId] });
      skipped.current = 0;
    },
    [persist, state],
  );

  const advance = useCallback(
    (answerValue?: string) => {
      if (!active || !flow) return;
      const index = active.step + 1;
      const answers =
        answerValue === undefined
          ? state.answers
          : { ...state.answers, [active.flowId]: answerValue };

      if (index >= flow.steps.length) {
        const { active: _drop, ...rest } = state;
        persist({
          ...rest,
          seen: [...state.seen, active.flowId],
          ...(answers === undefined ? {} : { answers }),
        });
        skipped.current = 0;
        return;
      }

      persist({
        ...state,
        active: { flowId: active.flowId, step: index },
        ...(answers === undefined ? {} : { answers }),
      });
    },
    [active, flow, persist, state],
  );

  /**
   * Step over an anchor that never appeared.
   *
   * Tolerated a couple of times, because one moved element is a gap worth
   * ignoring. Past that the flow no longer describes the app it was written
   * for, and showing the rest of it would be teaching a screen that is gone —
   * so it is retired rather than repaired at runtime.
   *
   * The counter is a REF rather than state: several steps can be skipped
   * faster than React re-renders, and a `useState` value read through the
   * closure would still be reporting zero on the third one.
   */
  const skip = useCallback(() => {
    if (!active) return;
    skipped.current += 1;
    if (skipped.current > MAX_SKIPPED_STEPS) {
      skipped.current = 0;
      finish(active.flowId);
      return;
    }
    advance();
  }, [active, advance, finish]);

  const offer = useCallback(
    (flowId: string) => {
      if (!loaded) return;
      if (state.active) return;
      if (state.seen.includes(flowId)) return;
      const candidate = byId.get(flowId);
      if (!candidate || candidate.steps.length === 0) return;
      if (candidate.priority === 'ambient') {
        if (ambientRun.current >= budget.ambientPerSession) return;
        ambientRun.current += 1;
      }
      skipped.current = 0;
      persist({ ...state, active: { flowId, step: 0 } });
    },
    [budget.ambientPerSession, byId, loaded, persist, state],
  );

  const completed = useCallback(
    (action: string) => {
      const advanceOn = step?.advance;
      if (!advanceOn || advanceOn === 'next') return;
      if (advanceOn.on !== action) return;
      advance();
    },
    [advance, step],
  );

  const api = useMemo<CoachApi>(
    () => ({
      offer,
      completed,
      dismiss: () => {
        if (active) finish(active.flowId);
      },
      next: () => advance(),
      answer: (value: string) => {
        if (active) onAnswer?.(active.flowId, value);
        advance(value);
      },
      skip,
      current: flow && step && active ? { flow, step, index: active.step } : null,
      answers: state.answers ?? {},
    }),
    [active, advance, completed, finish, flow, offer, onAnswer, skip, state.answers, step],
  );

  return <CoachContext.Provider value={api}>{children}</CoachContext.Provider>;
}

/**
 * An inert coach, used when there is no provider above.
 *
 * Deliberately not a thrown error. This package teaches the app; it must
 * never be able to take it down. A screen rendered outside a provider — in a
 * test, in a storybook, in some future embed — should work exactly as it
 * always did, with the coaching simply absent. That is the same principle as
 * a missing anchor skipping its step rather than freezing the flow.
 */
const inert: CoachApi = {
  offer: () => {},
  completed: () => {},
  dismiss: () => {},
  next: () => {},
  skip: () => {},
  answer: () => {},
  current: null,
  answers: {},
};

export function useCoach(): CoachApi {
  return useContext(CoachContext) ?? inert;
}
